import type { MigrationDto } from "@bafe/api-client";
import { OPERATIONAL_DEFAULTS } from "@bafe/config";
import { PlusIcon } from "@phosphor-icons/react";
import type { MigrationStatus } from "@bafe/types";
import {
	Button,
	Card,
	Dialog,
	EmptyState,
	Field,
	InlineNotice,
	Input,
	PageHeader,
	Select,
	SkeletonRows,
	StatusPill,
	useToast,
} from "@bafe/ui";
import { formatBytes } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorPanel } from "../components/ErrorPanel";
import { LinkButton } from "../components/LinkButton";
import { PasswordField } from "../components/PasswordField";
import { RowMenu } from "../components/RowMenu";
import { api } from "../lib/api";
import { presentError } from "../lib/errors";
import { queryKeys } from "../lib/query";
import { describeMigrationStatus, migrationPercent } from "../lib/status";

const ACTIVE_STATUSES: MigrationStatus[] = ["queued", "migrating", "retrying", "checking"];
const STARTABLE: MigrationStatus[] = ["draft", "ready", "failed", "completed_with_errors", "cancelled"];
const EDITABLE: MigrationStatus[] = ["draft", "ready", "failed"];

type Security = "ssl" | "starttls";

export function Migration() {
	const migrations = useQuery({
		queryKey: queryKeys.migrations,
		queryFn: () => api.listMigrations(),
		refetchInterval: (query) => {
			const data = query.state.data;
			return data?.some((migration) => ACTIVE_STATUSES.includes(migration.status)) ? 5_000 : false;
		},
	});

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				title="Bring your existing email to Key."
				description="Key copies your old messages and folders across. Your old mailbox is left exactly as it is."
				action={
					<LinkButton
						to="/mail/migration/new"
						variant="primary"
						iconLeft={<PlusIcon className="size-4" aria-hidden="true" />}
					>
						Set up a migration
					</LinkButton>
				}
			/>

			{migrations.isPending && <SkeletonRows rows={4} />}

			{migrations.isError && (
				<ErrorPanel
					error={migrations.error}
					title="We could not load your migrations."
					onRetry={() => void migrations.refetch()}
					retryPending={migrations.isFetching}
				/>
			)}

			{migrations.isSuccess &&
				(migrations.data.length === 0 ? (
					<Card>
						<EmptyState
							title="No migrations yet"
							description="Set one up and Key will copy your messages across in the background."
						/>
					</Card>
				) : (
					<Card flush>
						<MigrationTable rows={migrations.data} />
					</Card>
				))}
		</div>
	);
}

function MigrationTable({ rows }: { rows: MigrationDto[] }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [editing, setEditing] = useState<MigrationDto | null>(null);
	const [removing, setRemoving] = useState<MigrationDto | null>(null);

	function fail(title: string, error: unknown) {
		toast({ tone: "error", message: `${title} ${presentError(error).title}`.trim() });
	}

	const start = useMutation({
		mutationFn: (id: string) => api.startMigration(id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.migrations });
			toast({ tone: "success", message: "Migration started. You can leave this page." });
		},
		onError: (error) => fail("We could not start this migration.", error),
	});

	const pause = useMutation({
		mutationFn: (id: string) => api.pauseMigration(id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.migrations });
			toast({ tone: "success", message: "Migration paused. Messages already copied stay where they are." });
		},
		onError: (error) => fail("We could not pause this migration.", error),
	});

	const remove = useMutation({
		mutationFn: (id: string) => api.deleteMigration(id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.migrations });
			setRemoving(null);
			toast({ tone: "success", message: "Migration removed. Messages already copied stay in your Key mailbox." });
		},
	});

	function menu(migration: MigrationDto) {
		return (
			<RowMenu
				label={`Actions for ${migration.mailboxAddress}`}
				items={[
					{
						id: "start",
						label: start.isPending ? "Starting…" : "Start",
						disabled: !STARTABLE.includes(migration.status) || start.isPending || pause.isPending,
						onSelect: () => start.mutate(migration.id),
					},
					{
						id: "pause",
						label: pause.isPending ? "Pausing…" : "Pause",
						disabled: !ACTIVE_STATUSES.includes(migration.status) || start.isPending || pause.isPending,
						onSelect: () => pause.mutate(migration.id),
					},
					{
						id: "edit",
						label: "Edit",
						disabled: !EDITABLE.includes(migration.status),
						onSelect: () => setEditing(migration),
					},
					{
						id: "delete",
						label: "Delete",
						danger: true,
						onSelect: () => setRemoving(migration),
					},
				]}
			/>
		);
	}

	return (
		<>
			<table className="hidden w-full border-separate border-spacing-0 text-sm sm:table">
				<caption className="sr-only">Your migrations</caption>
				<thead>
					<tr className="border-b border-(--border-subtle)">
						<th
							scope="col"
							className="px-5 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-(--text-muted)"
						>
							Email
						</th>
						<th
							scope="col"
							className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-(--text-muted)"
						>
							IMAP
						</th>
						<th
							scope="col"
							className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-(--text-muted)"
						>
							Port
						</th>
						<th
							scope="col"
							className="min-w-52 px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-(--text-muted)"
						>
							Status
						</th>
						<th
							scope="col"
							className="px-5 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-(--text-muted)"
						>
							Action
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((migration) => (
						<tr
							key={migration.id}
							className="border-b border-(--border-subtle) last:border-0 transition-colors duration-150 hover:bg-(--surface-sunken)"
						>
							<td className="px-5 py-3.5 align-middle font-medium text-(--text-primary)">
								{migration.mailboxAddress}
							</td>
							<td className="px-4 py-3.5 align-middle font-mono text-sm text-(--text-secondary)">
								{migration.sourceHost}
							</td>
							<td className="px-4 py-3.5 align-middle tabular-nums text-(--text-secondary)">
								{migration.sourcePort ?? "—"}
							</td>
							<td className="px-4 py-3.5 align-middle">
								<MigrationStatusCell migration={migration} />
							</td>
							<td className="px-5 py-3.5 align-middle">{menu(migration)}</td>
						</tr>
					))}
				</tbody>
			</table>

			<ul className="flex flex-col sm:hidden">
				{rows.map((migration) => (
					<li key={migration.id} className="border-b border-(--border-subtle) px-4 py-3.5 last:border-0">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate font-medium text-(--text-primary)">{migration.mailboxAddress}</p>
								<p className="mt-0.5 truncate font-mono text-xs text-(--text-muted)">
									{migration.sourceHost}
									<span className="text-(--text-secondary)"> · {migration.sourcePort ?? "—"}</span>
								</p>
							</div>
							{menu(migration)}
						</div>
						<div className="mt-3">
							<MigrationStatusCell migration={migration} />
						</div>
					</li>
				))}
			</ul>

			<EditMigrationDialog migration={editing} onClose={() => setEditing(null)} />

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title="Delete this migration?"
				description="Messages already copied stay in your Key mailbox. Nothing is removed from your old provider."
				confirmLabel="Delete migration"
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing.id);
				}}
			/>
		</>
	);
}

function MigrationStatusCell({ migration }: { migration: MigrationDto }) {
	const status = describeMigrationStatus(migration);
	const percent = migrationPercent(migration) ?? 0;

	return (
		<div className="flex w-full max-w-64 flex-col gap-1.5">
			<div className="flex items-center justify-between gap-3">
				<StatusPill tone={status.tone}>{status.label}</StatusPill>
				<span className="tabular-nums text-sm font-semibold tracking-tight text-(--text-primary)">
					{percent}%
				</span>
			</div>
			<div
				role="progressbar"
				aria-valuenow={percent}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-label={`Migration progress for ${migration.mailboxAddress}`}
				className="h-1.5 w-full overflow-hidden rounded-full bg-(--surface-sunken)"
			>
				<div
					className="h-full rounded-full bg-(--color-brand-500) transition-[width] duration-500 ease-out"
					style={{ width: `${percent}%` }}
				/>
			</div>
			{migration.lastError ? (
				<p className="text-xs leading-snug text-(--color-critical-700)">{migration.lastError}</p>
			) : (
				<p className="text-xs text-(--text-muted)">
					{migration.processedMessages.toLocaleString()} of {migration.totalMessages.toLocaleString()}{" "}
					messages
					{migration.bytesProcessed > 0 ? ` · ${formatBytes(migration.bytesProcessed)}` : ""}
				</p>
			)}
		</div>
	);
}

function EditMigrationDialog({
	migration,
	onClose,
}: {
	migration: MigrationDto | null;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [host, setHost] = useState("");
	const [port, setPort] = useState("993");
	const [security, setSecurity] = useState<Security>("ssl");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");

	useEffect(() => {
		if (!migration) return;
		setHost(migration.sourceHost);
		setPort(String(migration.sourcePort ?? 993));
		setSecurity(migration.sourceSecurity === "starttls" ? "starttls" : "ssl");
		setUsername("");
		setPassword("");
	}, [migration]);

	const save = useMutation({
		mutationFn: () => {
			if (!migration) throw new Error("missing");
			const nextUsername = username.trim();
			return api.updateMigration(migration.id, {
				sourceHost: host.trim(),
				sourcePort: Number(port),
				sourceSecurity: security,
				...(nextUsername && password
					? { sourceUsername: nextUsername, sourcePassword: password }
					: {}),
			});
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.migrations });
			setPassword("");
			toast({ tone: "success", message: "IMAP details saved." });
			onClose();
		},
	});

	if (!migration) return null;

	const canSave = host.trim().length > 0 && Number(port) > 0 && (username.trim() === "") === (password === "");

	return (
		<Dialog
			open
			onClose={() => {
				if (!save.isPending) onClose();
			}}
			title={`Edit ${migration.mailboxAddress}`}
			description="Change the IMAP server this migration copies from. Leave the password blank to keep the one already saved."
			dismissable={!save.isPending}
			footer={
				<>
					<Button variant="ghost" onClick={onClose} disabled={save.isPending}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!canSave}
						pending={save.isPending}
						pendingLabel="Saving…"
						onClick={() => save.mutate()}
					>
						Save
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-4">
				<div className="grid gap-4 sm:grid-cols-[1fr_7rem_10rem]">
					<Field label="IMAP server" required>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={host}
								required
								autoComplete="off"
								autoCapitalize="none"
								spellCheck={false}
								placeholder="imap.example.com"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setHost(event.target.value)}
							/>
						)}
					</Field>
					<Field label="Port" required>
						{({ id }) => (
							<Input
								id={id}
								type="number"
								inputMode="numeric"
								value={port}
								required
								onChange={(event) => setPort(event.target.value)}
							/>
						)}
					</Field>
					<Field label="Security" required>
						{({ id }) => (
							<Select
								id={id}
								value={security}
								onChange={(event) =>
									setSecurity(event.target.value === "starttls" ? "starttls" : "ssl")
								}
							>
								<option value="ssl">SSL/TLS</option>
								<option value="starttls">STARTTLS</option>
							</Select>
						)}
					</Field>
				</div>

				<Field label="Username at your old provider" help="Leave blank to keep the saved username.">
					{({ id }) => (
						<Input
							id={id}
							value={username}
							autoComplete="off"
							autoCapitalize="none"
							spellCheck={false}
							onChange={(event) => setUsername(event.target.value)}
						/>
					)}
				</Field>

				<PasswordField
					label="Password at your old provider"
					value={password}
					onChange={setPassword}
					autoComplete="off"
				/>

				<InlineNotice tone="info" title="This password is temporary">
					Key holds it only for as long as the migration runs, and for at most{" "}
					{OPERATIONAL_DEFAULTS.migration_credential_ttl_hours} hours.
				</InlineNotice>

				{save.isError && <ErrorPanel error={save.error} title="We could not save those details." />}
			</div>
		</Dialog>
	);
}
